import memoize from 'lodash.memoize';
import { createSelector } from 'reselect';
import mapById from '../../utility/mapById';
import * as technical from './iiif/technical';
import * as descriptive from './iiif/descriptive';
import * as linking from './iiif/linking';
import * as structural from './iiif/structural';
import { getAllAnnotationPages, getAllCanvases, getAllCanvasReferences, getAllContentResources, getAllRanges, getAllResources, getAllServices } from '../all';
import mapByIdOrId from '../../utility/mapByIdOrId';
import mapAllResources from '../../utility/mapAllResources';
import mapAllById from '../../utility/mapAllById';

var manifest = memoize(function (selector) {
  /**
   * Technical properties
   *
   * - getId
   * - getType
   * - getViewingDirection
   * - getBehavior
   */
  var getId = createSelector(selector, technical.getId);

  var getType = createSelector(selector, technical.getType);

  var getViewingDirection = createSelector(selector, technical.getViewingDirection);

  var getBehavior = createSelector(selector, technical.getBehavior);

  /**
   * Descriptive Properties
   *
   * - getLabel
   * - getMetadata
   * - getSummary
   * - getThumbnail
   * - getPosterCanvas
   * - getRequiredStatement
   * - getRights
   * - getNavDate
   */
  var getLabel = createSelector(selector, descriptive.getLabel);

  var getMetadata = createSelector(selector, descriptive.getMetadata);

  var getSummary = createSelector(selector, descriptive.getSummary);

  var getThumbnailId = createSelector(selector, descriptive.getThumbnailId);

  var getThumbnail = createSelector(getThumbnailId, getAllContentResources, mapByIdOrId);

  var getPosterCanvasId = createSelector(selector, descriptive.getPosterCanvas);

  var getPosterCanvas = createSelector(getPosterCanvasId, getAllCanvases, mapById);

  var getRequiredStatement = createSelector(selector, descriptive.getRequiredStatement);

  var getRights = createSelector(selector, descriptive.getRights);

  var getNavDate = createSelector(selector, descriptive.getNavDate);

  /**
   * Linking properties
   *
   * - getSeeAlso
   * - getService
   * - getLogo
   * - getHomepage
   * - getRendering
   * - getPartOf
   * - getStart
   */
  var getSeeAlsoIds = createSelector(selector, linking.getSeeAlso);
  var getSeeAlso = createSelector(getSeeAlsoIds, getAllContentResources, mapAllById);

  var getServiceIds = createSelector(selector, linking.getService);
  var getService = createSelector(getServiceIds, getAllServices, mapAllById);

  var getLogoIds = createSelector(selector, linking.getLogo);
  var getLogo = createSelector(getLogoIds, getAllContentResources, mapAllById);

  var getHomepageId = createSelector(selector, linking.getHomepage);
  var getHomepage = createSelector(getHomepageId, getAllContentResources, mapById);

  var getRenderingIds = createSelector(selector, linking.getRendering);
  var getRendering = createSelector(getRenderingIds, getAllContentResources, mapAllById);

  var getPartOfId = createSelector(selector, linking.getPartOf);
  var getPartOf = createSelector(getPartOfId, getAllResources, mapAllResources);

  var getStartId = createSelector(selector, linking.getStart);
  var getStart = createSelector(getStartId, getAllCanvases, getAllCanvasReferences, function (start, allCanvases, allCanvasReferences) {
    if (!start) {
      return null;
    }
    if (start.schema === 'canvas') {
      return allCanvases[start.id];
    }
    return allCanvasReferences[start.id];
  });

  /**
   * Structural Properties
   *
   * - getItems
   * - getStructures
   * - getAnnotations
   */
  var getItemIds = createSelector(selector, structural.getItems);
  var getItems = createSelector(getItemIds, getAllCanvases, mapAllById);

  var getCanvasIds = getItemIds;
  var getCanvases = getItems;

  var getStructureIds = createSelector(selector, structural.getStructures);
  var getStructures = createSelector(getStructureIds, getAllRanges, mapAllById);

  var getAnnotationIds = createSelector(selector, structural.getAnnotations);
  var getAnnotations = createSelector(getAnnotationIds, getAllAnnotationPages, mapAllById);

  return {
    getId: getId,
    getType: getType,
    getViewingDirection: getViewingDirection,
    getBehavior: getBehavior,
    getLabel: getLabel,
    getMetadata: getMetadata,
    getSummary: getSummary,
    getThumbnailId: getThumbnailId,
    getThumbnail: getThumbnail,
    getPosterCanvasId: getPosterCanvasId,
    getPosterCanvas: getPosterCanvas,
    getRequiredStatement: getRequiredStatement,
    getRights: getRights,
    getNavDate: getNavDate,
    getSeeAlsoIds: getSeeAlsoIds,
    getSeeAlso: getSeeAlso,
    getServiceIds: getServiceIds,
    getService: getService,
    getLogoIds: getLogoIds,
    getLogo: getLogo,
    getHomepageId: getHomepageId,
    getHomepage: getHomepage,
    getRenderingIds: getRenderingIds,
    getRendering: getRendering,
    getPartOfId: getPartOfId,
    getPartOf: getPartOf,
    getStartId: getStartId,
    getStart: getStart,
    getItemIds: getItemIds,
    getItems: getItems,
    getCanvasIds: getCanvasIds,
    getCanvases: getCanvases,
    getStructureIds: getStructureIds,
    getStructures: getStructures,
    getAnnotationIds: getAnnotationIds,
    getAnnotations: getAnnotations
  };
});

export default manifest;